import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Link,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DangerousOutlinedIcon from '@mui/icons-material/DangerousOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import { getEmergencyInfo } from '../../services/teacher';

function telHref(phone) {
  if (!phone || !String(phone).trim()) return null;
  const cleaned = String(phone).replace(/[^\d+]/g, '');
  if (!cleaned.replace('+', '')) return null;
  return `tel:${cleaned}`;
}

function hasAnyEmergencyData(s) {
  const med =
    s.allergies ||
    s.medical_alerts ||
    s.medications ||
    s.dietary_restrictions;
  if (med) return true;
  const contacts = s.emergency_contacts || [];
  return contacts.some((c) => (c.name && c.name.trim()) || (c.phone && c.phone.trim()));
}

function suggestsAllergyHighlight(s) {
  if (s.allergies && String(s.allergies).trim()) return true;
  const m = (s.medical_alerts || '').toLowerCase();
  return /\ballerg/.test(m) || /\bpeanut/.test(m) || /\bnut\b/.test(m) || /\bepi\b/.test(m);
}

function FieldRow({ icon, label, value, emptyLabel, color, borderColor }) {
  const show = value != null && String(value).trim() !== '';
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
      <Box sx={{ color: borderColor || 'text.secondary', mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}:{' '}
        </Typography>
        {show ? (
          <Typography component="span" variant="body2" color={color || 'text.primary'}>
            {String(value).trim()}
          </Typography>
        ) : (
          <Typography component="span" variant="body2" color="text.disabled">
            {emptyLabel}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export default function EmergencyInfoPage() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [onlyAlerts, setOnlyAlerts] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getEmergencyInfo()
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.results || res.data || [];
        setStudents(Array.isArray(list) ? list : []);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const anyClasswideMedicalData = useMemo(
    () => students.length > 0 && students.some(hasAnyEmergencyData),
    [students]
  );

  const filtered = useMemo(() => {
    let list = students;
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const fn = (s.first_name || '').toLowerCase();
        const ln = (s.last_name || '').toLowerCase();
        return fn.includes(q) || ln.includes(q) || `${fn} ${ln}`.includes(q);
      });
    }
    if (onlyAlerts) {
      list = list.filter(hasAnyEmergencyData);
    }
    return list;
  }, [students, query, onlyAlerts]);

  if (loading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((k) => (
          <Card key={k} variant="outlined">
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="60%" />
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: '100%' }}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        Emergency information
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Medical and contact details for students in your assigned classroom. Read-only — contact your administrator to
        update records.
      </Typography>

      {students.length > 0 && !anyClasswideMedicalData && (
        <Alert severity="info">
          No emergency information on file for your students. Contact your administrator if this seems incorrect.
        </Alert>
      )}

      {students.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            fullWidth
            label="Search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="First or last name"
          />
          <FormControlLabel
            control={<Switch checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} color="primary" />}
            label="Show only students with info on file"
          />
        </Stack>
      )}

      {students.length === 0 ? (
        <Alert severity="info">No students in your classroom roster.</Alert>
      ) : (
        <Stack spacing={2}>
          {filtered.length === 0 ? (
            <Alert severity="info">No students match your search or filter.</Alert>
          ) : (
            filtered.map((s) => {
              const allergyStyle = suggestsAllergyHighlight(s);
              return (
                <Card
                  key={s.id}
                  variant="outlined"
                  sx={(theme) => ({
                    borderLeft: allergyStyle ? 4 : 0,
                    borderLeftColor: allergyStyle ? theme.palette.error.light : 'transparent',
                    boxShadow: allergyStyle ? 1 : 0,
                    bgcolor: allergyStyle ? alpha(theme.palette.error.main, 0.08) : theme.palette.background.paper,
                  })}
                >
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {s.first_name} {s.last_name}
                    </Typography>

                    <FieldRow
                      icon={<DangerousOutlinedIcon fontSize="small" />}
                      label="Allergies"
                      value={s.allergies}
                      emptyLabel="None on file"
                      color="error.main"
                      borderColor="error.main"
                    />
                    <FieldRow
                      icon={<LocalHospitalOutlinedIcon fontSize="small" />}
                      label="Medical"
                      value={s.medical_alerts}
                      emptyLabel="None on file"
                      color="warning.dark"
                      borderColor="warning.main"
                    />
                    <FieldRow
                      icon={<MedicationOutlinedIcon fontSize="small" />}
                      label="Medications"
                      value={s.medications}
                      emptyLabel="None on file"
                      borderColor="text.secondary"
                    />
                    <FieldRow
                      icon={<RestaurantOutlinedIcon fontSize="small" />}
                      label="Dietary"
                      value={s.dietary_restrictions}
                      emptyLabel="None on file"
                      borderColor="text.secondary"
                    />

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Emergency contacts
                      </Typography>
                      {(s.emergency_contacts || []).length === 0 ? (
                        <Typography variant="body2" color="text.disabled">
                          None on file
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {(s.emergency_contacts || []).map((c, idx) => {
                            const href = telHref(c.phone);
                            return (
                              <Typography key={`${c.name}-${idx}`} variant="body2" sx={{ wordBreak: 'break-word' }}>
                                <Box component="span" sx={{ fontWeight: 500 }}>
                                  {c.name || '—'}
                                </Box>
                                {c.relationship ? (
                                  <Box component="span" color="text.secondary">
                                    {' '}
                                    ({c.relationship})
                                  </Box>
                                ) : null}
                                {c.phone ? (
                                  <>
                                    {' '}
                                    {href ? (
                                      <Link href={href} underline="hover" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                        {c.phone}
                                      </Link>
                                    ) : (
                                      <Box component="span">{c.phone}</Box>
                                    )}
                                  </>
                                ) : null}
                              </Typography>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      )}
    </Stack>
  );
}
